import type {
  ApproveResponse,
  ExpenseDto,
  InvoiceDto,
  MemberDto,
  NoticeDto,
  NotificationDto,
  PassDto,
  PaymentDto,
  SignupDto,
  TicketDto,
  UnitDto,
  VisitDto,
} from '@aawas/contracts';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api } from './api';
import { compressImage } from './image';

const qs = (o: Record<string, string | number | boolean | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

// ───────────── visitors & passes ─────────────

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export const useVisits = (params: { status?: string; open?: boolean; unitId?: string; limit?: number } = {}, refetchInterval?: number) =>
  useQuery({
    queryKey: ['visits', params],
    queryFn: () => api<Page<VisitDto>>(`/visits${qs({ ...params, open: params.open ? 'true' : undefined, limit: params.limit ?? 50 })}`),
    refetchInterval,
  });

export const useVisit = (id: string) => useQuery({ queryKey: ['visits', 'one', id], queryFn: () => api<VisitDto>(`/visits/${id}`) });

export const useVisitEvents = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: ['visits', 'events', id],
    enabled,
    queryFn: () => api<{ valid: boolean; events: Array<{ type: string; actorUserId: string | null; createdAt: string; payload: unknown }> }>(`/visits/${id}/events`),
  });

export const usePasses = (status = 'active') => useQuery({ queryKey: ['passes', status], queryFn: () => api<PassDto[]>(`/passes${qs({ status })}`) });

function useInvalidating<TVars, TRes>(fn: (v: TVars) => Promise<TRes>, keys: QueryKey[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => keys.forEach((k) => void qc.invalidateQueries({ queryKey: k })),
  });
}

export const useDecide = () =>
  useInvalidating((v: { id: string; decision: 'approve' | 'reject'; note?: string }) => api<VisitDto>(`/visits/${v.id}/decision`, { body: { decision: v.decision, note: v.note } }), [['visits'], ['notifications']]);

export const useVisitAction = () =>
  useInvalidating((v: { id: string; action: 'enter' | 'exit' | 'cancel'; overrideReason?: string }) => api<VisitDto>(`/visits/${v.id}/${v.action}`, { body: v.overrideReason ? { overrideReason: v.overrideReason } : {} }), [['visits']]);

export const useCreatePass = () =>
  useInvalidating(
    (b: { unitId: string; visitorName: string; visitorPhone?: string; kind: string; validUntil: string; maxUses: number; vehicleNo?: string }) => api<PassDto>('/passes', { body: b }),
    [['passes']],
  );
export const useRevokePass = () => useInvalidating((id: string) => api(`/passes/${id}/revoke`, { body: {} }), [['passes']]);

// ───────────── helpdesk ─────────────

export const useTickets = (params: { status?: string; mine?: boolean; assignedToMe?: boolean; limit?: number } = {}, refetchInterval?: number) =>
  useQuery({
    queryKey: ['tickets', params],
    queryFn: () =>
      api<Page<TicketDto>>(`/tickets${qs({ status: params.status, mine: params.mine ? 'true' : undefined, assignedToMe: params.assignedToMe ? 'true' : undefined, limit: params.limit ?? 50 })}`),
    refetchInterval,
  });

export interface TicketDetail extends TicketDto {
  comments: Array<{ id: string; authorId: string; authorName: string | null; body: string; internal: boolean; createdAt: string }>;
}
export const useTicket = (id: string, refetchInterval?: number) =>
  useQuery({ queryKey: ['tickets', 'one', id], queryFn: () => api<TicketDetail>(`/tickets/${id}`), refetchInterval });

export const useCategories = () => useQuery({ queryKey: ['categories'], queryFn: () => api<Array<{ id: string; name: string; slaHours: number }>>('/ticket-categories'), staleTime: 300_000 });

export const useCreateTicket = () =>
  useInvalidating((b: { title: string; description: string; categoryId?: string; unitId?: string; priority: string }) => api<TicketDto>('/tickets', { body: b }), [['tickets']]);
export const useAddComment = () =>
  useInvalidating((v: { id: string; body: string; internal?: boolean }) => api(`/tickets/${v.id}/comments`, { body: { body: v.body, internal: !!v.internal } }), [['tickets']]);
export const useUpdateTicket = () =>
  useInvalidating((v: { id: string; patch: Record<string, unknown> }) => api<TicketDto>(`/tickets/${v.id}`, { method: 'PATCH', body: v.patch }), [['tickets']]);
export const useTicketAction = () =>
  useInvalidating((v: { id: string; action: 'close' | 'reopen'; rating?: number; reason?: string }) => api<TicketDto>(`/tickets/${v.id}/action`, { body: v }), [['tickets']]);
export const uploadAttachment = async (ticketId: string, original: File) => {
  const form = new FormData();
  form.append('file', await compressImage(original));
  return api<{ id: string }>(`/tickets/${ticketId}/attachments`, { form });
};

// ───────────── community ─────────────

export const useNotices = (includeExpired = false) =>
  useQuery({ queryKey: ['notices', includeExpired], queryFn: () => api<NoticeDto[]>(`/notices${qs({ includeExpired: includeExpired ? 'true' : undefined })}`) });
export const useCreateNotice = () =>
  useInvalidating((b: { title: string; body: string; category: string; audience: string; pinned: boolean }) => api<NoticeDto>('/notices', { body: b }), [['notices']]);
export const useDeleteNotice = () => useInvalidating((id: string) => api(`/notices/${id}`, { method: 'DELETE' }), [['notices']]);

export const useUnits = () => useQuery({ queryKey: ['units'], queryFn: () => api<UnitDto[]>('/units'), staleTime: 300_000 });
export interface CommunityInfo {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  timezone: string;
  settings: {
    approvalTimeoutSec: number;
    noResponsePolicy: 'guard_discretion' | 'deny';
    photoRetentionDays: number;
    invoiceDueDay: number;
    tallyCompanyName: string;
    billingFrequency: 'monthly' | 'quarterly' | 'half_yearly';
  };
}
export const useCommunity = () => useQuery({ queryKey: ['community'], queryFn: () => api<CommunityInfo>('/community'), staleTime: 300_000 });
export const useUpdateCommunity = () =>
  useInvalidating((b: { name?: string; settings?: Partial<CommunityInfo['settings']> }) => api<CommunityInfo>('/community', { method: 'PATCH', body: b }), [['community']]);

export const useMembers = (role?: string, enabled = true) => useQuery({ queryKey: ['members', role], enabled, queryFn: () => api<MemberDto[]>(`/members${qs({ role })}`) });
export const useAddMember = () =>
  useInvalidating((b: { phone: string; name: string; role: string; unitId?: string; title?: string; canApproveVisitors?: boolean }) => api<MemberDto>('/members', { body: b }), [['members']]);
export const useUpdateMember = () =>
  useInvalidating((v: { id: string; patch: Record<string, unknown> }) => api(`/members/${v.id}`, { method: 'PATCH', body: v.patch }), [['members']]);
export const issueLoginCode = (membershipId: string) => api<{ code: string; expiresAt: string; phone: string }>(`/members/${membershipId}/login-code`, { body: {} });
export const useBulkMembers = () =>
  useInvalidating(
    (rows: Array<{ block: string; unit: string; name: string; phone: string; role: 'owner' | 'tenant' }>) =>
      api<{ created: number; skipped: Array<{ row: number; reason: string }> }>('/members/bulk', { body: { rows } }),
    [['members']],
  );
export const useBulkUnits = () =>
  useInvalidating((blocks: Array<{ block: string; units: string[]; areaSqft?: number }>) => api<{ blocks: number; units: number }>('/units/bulk', { body: { blocks } }), [['units']]);

export const useHousehold = () => useQuery({ queryKey: ['household'], queryFn: () => api<MemberDto[]>('/household') });
export const useAddHousehold = () =>
  useInvalidating((b: { unitId: string; phone: string; name: string; canApproveVisitors: boolean }) => api('/household/members', { body: b }), [['household']]);
export const useRemoveHousehold = () => useInvalidating((id: string) => api(`/household/members/${id}`, { method: 'DELETE' }), [['household']]);

// ───────────── notifications ─────────────

export const useNotifications = () =>
  useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<{ items: NotificationDto[]; nextCursor: string | null; unreadCount: number }>('/notifications?limit=50', { noCommunity: true }),
    refetchInterval: 60_000,
  });
export const useMarkRead = () => useInvalidating((v: { ids?: string[]; all?: boolean }) => api('/notifications/read', { body: v, noCommunity: true }), [['notifications']]);

// ───────────── finance (committee) ─────────────

export interface FinanceAccount { id: string; code: string; name: string; type: 'asset' | 'liability' | 'equity' | 'income' | 'expense'; isBank: boolean; active: boolean }
export interface ChargeHead { id: string; name: string; incomeAccountId: string; incomeAccountName: string; basis: 'fixed' | 'per_sqft'; amountPaise: number; active: boolean }
export interface DuesRow { unitId: string; unitLabel: string; invoicedPaise: number; paidPaise: number; balancePaise: number; oldestUnpaidDue: string | null; daysOverdue: number }
export interface DuesResponse { items: DuesRow[]; totalDuePaise: number; totalAdvancePaise: number; overdueUnits: number }
export interface TrialBalance { asOf: string; rows: Array<{ code: string; name: string; type: string; debitPaise: number; creditPaise: number }>; totalDebitPaise: number; totalCreditPaise: number; balanced: boolean }
export interface IncomeExpenditure { from: string; to: string; income: Array<{ code: string; name: string; amountPaise: number }>; expenses: Array<{ code: string; name: string; amountPaise: number }>; totalIncomePaise: number; totalExpensePaise: number; surplusPaise: number }
export interface MyFinance { balancePaise: number; invoices: InvoiceDto[]; payments: PaymentDto[] }

export const useFinanceAccounts = () => useQuery({ queryKey: ['finance', 'accounts'], queryFn: () => api<FinanceAccount[]>('/finance/accounts'), staleTime: 300_000 });
export const useChargeHeads = () => useQuery({ queryKey: ['finance', 'heads'], queryFn: () => api<ChargeHead[]>('/finance/charge-heads') });
export const useUpdateChargeHead = () =>
  useInvalidating((v: { id: string; patch: { amountPaise?: number; active?: boolean; name?: string; basis?: 'fixed' | 'per_sqft' } }) => api<ChargeHead>(`/finance/charge-heads/${v.id}`, { method: 'PATCH', body: v.patch }), [['finance']]);
export const useDues = () => useQuery({ queryKey: ['finance', 'dues'], queryFn: () => api<DuesResponse>('/finance/dues') });
export const useInvoices = (params: { period?: string; status?: string; unitId?: string } = {}) =>
  useQuery({ queryKey: ['finance', 'invoices', params], queryFn: () => api<InvoiceDto[]>(`/finance/invoices${qs({ ...params, limit: 500 })}`) });
export const useGenerateInvoices = () =>
  useInvalidating((v: { period: string; dueDate?: string; unitIds?: string[] }) => api<{ created: number; skippedExisting: number; totalPaise: number }>('/finance/invoices/generate', { body: v }), [['finance'], ['notifications']]);
export const useVoidInvoice = () => useInvalidating((v: { id: string; reason: string }) => api(`/finance/invoices/${v.id}/void`, { body: { reason: v.reason } }), [['finance']]);
export const useVoidInvoices = () =>
  useInvalidating((v: { ids: string[]; reason: string }) => api<{ voided: number; skippedPaid: number }>("/finance/invoices/void", { body: v }), [["finance"]]);
export const usePayments = (params: { unitId?: string } = {}) =>
  useQuery({ queryKey: ['finance', 'payments', params], queryFn: () => api<PaymentDto[]>(`/finance/payments${qs({ ...params, limit: 300 })}`) });
export const useRecordPayment = () =>
  useInvalidating(
    (b: { unitId: string; amountPaise: number; mode: string; receivedOn: string; reference?: string; payerName?: string; notes?: string }) => api<PaymentDto>('/finance/payments', { body: b }),
    [['finance']],
  );
export const useReversePayment = () => useInvalidating((v: { id: string; reason: string }) => api(`/finance/payments/${v.id}/reverse`, { body: { reason: v.reason } }), [['finance']]);
export const useExpenses = () => useQuery({ queryKey: ['finance', 'expenses'], queryFn: () => api<ExpenseDto[]>('/finance/expenses?limit=300') });
export const useRecordExpense = () =>
  useInvalidating(
    (b: { expenseDate: string; expenseAccountId: string; paidFromAccountId: string; amountPaise: number; payee: string; reference?: string; notes?: string }) => api<ExpenseDto>('/finance/expenses', { body: b }),
    [['finance']],
  );
export const useReverseExpense = () => useInvalidating((v: { id: string; reason: string }) => api(`/finance/expenses/${v.id}/reverse`, { body: { reason: v.reason } }), [['finance']]);
export const useTrialBalance = (asOf: string) => useQuery({ queryKey: ['finance', 'tb', asOf], queryFn: () => api<TrialBalance>(`/finance/reports/trial-balance?asOf=${asOf}`) });
export const useIncomeExpenditure = (from: string, to: string) =>
  useQuery({ queryKey: ['finance', 'ie', from, to], queryFn: () => api<IncomeExpenditure>(`/finance/reports/income-expenditure?from=${from}&to=${to}`) });
export const useLock = () => useQuery({ queryKey: ['finance', 'lock'], queryFn: () => api<{ lockedThrough: string | null }>('/finance/lock') });
export const useSetLock = () => useInvalidating((through: string) => api<{ lockedThrough: string }>('/finance/lock', { body: { through } }), [['finance']]);
export const useMyFinance = () => useQuery({ queryKey: ['finance', 'my'], queryFn: () => api<MyFinance>('/finance/my') });

// ───────────── platform operator: society sign-up requests ─────────────
// These are account-level, not society-level, so they carry no community/role headers.

export const usePlatformSignups = (status: 'pending' | 'approved' | 'rejected', enabled = true) =>
  useQuery({ queryKey: ['platform', 'signups', status], enabled, queryFn: () => api<SignupDto[]>(`/platform/signups${qs({ status })}`, { noCommunity: true }) });
export const useApproveSignup = () =>
  useInvalidating((id: string) => api<ApproveResponse>(`/platform/signups/${id}/approve`, { body: {}, noCommunity: true }), [['platform']]);
export const useRejectSignup = () =>
  useInvalidating((v: { id: string; note?: string }) => api<SignupDto>(`/platform/signups/${v.id}/reject`, { body: v.note ? { note: v.note } : {}, noCommunity: true }), [['platform']]);
export const useReissueSignupCode = () =>
  useInvalidating((id: string) => api<ApproveResponse>(`/platform/signups/${id}/login-code`, { body: {}, noCommunity: true }), [['platform']]);

// ───────────── maintenance basis, expense heads, spending ─────────────

export interface UnitArea { unitId: string; unitLabel: string; areaSqft: number | null }
export const useUnitAreas = () => useQuery({ queryKey: ['finance', 'areas'], queryFn: () => api<UnitArea[]>('/finance/unit-areas') });
export const useSetUnitAreas = () =>
  useInvalidating((areas: Array<{ unitId: string; areaSqft: number | null }>) => api('/finance/unit-areas', { method: 'PUT', body: { areas } }), [['finance'], ['units']]);

export interface ExpenseHead { id: string; code: string; name: string; active: boolean; isSystem: boolean }
export const useExpenseHeads = () => useQuery({ queryKey: ['finance', 'expense-heads'], queryFn: () => api<ExpenseHead[]>('/finance/expense-heads') });
export const useCreateExpenseHead = () => useInvalidating((name: string) => api<ExpenseHead>('/finance/expense-heads', { body: { name } }), [['finance']]);
export const useUpdateExpenseHead = () =>
  useInvalidating((v: { id: string; patch: { name?: string; active?: boolean } }) => api<ExpenseHead>(`/finance/expense-heads/${v.id}`, { method: 'PATCH', body: v.patch }), [['finance']]);

export interface MyMaintenance {
  frequency: 'monthly' | 'quarterly' | 'half_yearly';
  monthsPerCycle: number;
  units: Array<{
    unitId: string;
    unitLabel: string;
    areaSqft: number | null;
    charges: Array<{ name: string; basis: 'fixed' | 'per_sqft'; ratePaise: number; perMonthPaise: number | null }>;
    perMonthPaise: number | null;
    perCyclePaise: number | null;
  }>;
}
export const useMyMaintenance = () => useQuery({ queryKey: ['finance', 'my-maintenance'], queryFn: () => api<MyMaintenance>('/finance/my/maintenance') });

export interface Spending {
  from: string;
  to: string;
  totalAreaSqft: number;
  unitsMissingArea: number;
  openingPaise: number;
  collectedPaise: number;
  spentPaise: number;
  closingPaise: number;
  heads: Array<{ accountId: string; name: string; spentPaise: number }>;
  months: Array<{ month: string; openingPaise: number; collectedPaise: number; spentPaise: number; closingPaise: number; byHead: Array<{ accountId: string; spentPaise: number }> }>;
}
export const useSpending = (from: string, to: string) =>
  useQuery({ queryKey: ['finance', 'spending', from, to], enabled: !!from && !!to && from <= to, queryFn: () => api<Spending>(`/finance/spending?from=${from}&to=${to}`) });
